import type { FunctionComponent, RefObject } from 'react';
import React from 'react';

import { LazyPluginButton } from './LazyPluginButton';
import type { Board, PluginBoardButton } from './PluginHeaderButton.types';
import { usePluginHeaderButtonsQuery } from './PluginHeaderButtonsQuery.generated';
import { usePluginBoardButtons } from './usePluginBoardButtons';

export interface PluginHeaderButtonsProps {
  openPluginSettings: (pluginId: string) => void;
  board: Board;
  container: Element | RefObject<HTMLElement>;
  isCollapsedOnHeader?: boolean;
}

export const PluginHeaderButtons: FunctionComponent<PluginHeaderButtonsProps> = ({
  openPluginSettings,
  board,
  container,
  isCollapsedOnHeader = false,
}: PluginHeaderButtonsProps) => {
  const idBoard = board.id;
  const { data } = usePluginHeaderButtonsQuery({
    variables: { idBoard },
    waitOn: ['CurrentBoardFull'],
  });
  const hiddenPluginBoardButtons: string[] =
    data?.board?.prefs?.hiddenPluginBoardButtons ?? [];

  const { isLoading, pluginsData, error } = usePluginBoardButtons(
    board,
    hiddenPluginBoardButtons,
  );

  if (error) {
    console.warn(error);
  }

  const { groupedBoardButtons, pluginsById } = pluginsData || {
    numPlugins: 0,
    groupedBoardButtons: {},
    pluginsById: {},
  };

  const pluginHeaderButtons = Object.values(groupedBoardButtons).map(
    (pluginButtonArr: PluginBoardButton[]) => {
      const isGrouped = pluginButtonArr.length > 1;
      return pluginButtonArr.map((pluginButton, idx) => {
        const pluginId = pluginButton.idPlugin;
        const uniqueKey = pluginId + idx;
        const isFirst = idx === 0;
        const plugin = pluginsById[pluginId];
        const enabledHeaderButton = !hiddenPluginBoardButtons.includes(
          pluginId,
        );
        if (plugin && enabledHeaderButton) {
          return (
            <LazyPluginButton
              key={uniqueKey}
              pluginId={pluginId}
              board={board}
              plugin={plugin}
              pluginBoardButton={pluginButton}
              el={container}
              onHeader={true}
              isCollapsedOnHeader={isCollapsedOnHeader}
              enabledHeaderButton={enabledHeaderButton}
              isGrouped={isGrouped}
              isFirst={isFirst}
              openPluginSettings={openPluginSettings}
            />
          );
        }
      });
    },
  );

  if (isLoading) return null;

  return <>{pluginHeaderButtons}</>;
};
